import React, { useCallback, useEffect, useState } from 'react'
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import LazyHydrate from 'react-lazy-hydration'
import { Button, ButtonProps } from '@/atoms/Button'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { AsDiv, ParagraphSM, TitleMD } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils'
import { AD_TESTING_COOKIE } from '@/constants/cookies'
import { Durations } from '@/constants/durations'
import { AngelDialog } from '@/molecules/AngelDialog'
import { useKonamiTapper } from '@/organisms/Footer/ExperimentVariantPicker/useKonamiTapper'
import { useUser } from '@/services/UserService'
import { useSafeAnalytics } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'

export interface ExperimentVariantPickerProps {
  logoColor?: IconColor
}

const DEVELOPER_MODE_COOKIE_NAME = 'enable_developer_mode'

export const isDevMode = () => Boolean(getCookie(DEVELOPER_MODE_COOKIE_NAME))

const InternalExperimentVariantPicker: React.FC<ExperimentVariantPickerProps> = ({ logoColor = 'white' }) => {
  const { isKonamiMaster, increaseTapCount } = useKonamiTapper({ enabled: isDevMode() })
  const [isDialogOpen, setDialogOpen] = useState(false)
  const { getState } = useSafeAnalytics()
  const { anonymousId } = getState().user
  const { userId } = useUser()
  const { reload } = useSafeRouter()
  const [isForcingAd, setIsForcingAd] = useState(Boolean(getCookie(AD_TESTING_COOKIE)))

  useEffect(() => {
    if (isKonamiMaster) {
      setCookie(DEVELOPER_MODE_COOKIE_NAME, true, { maxAge: Durations.ONE_WEEK_IN_SECONDS })
    }
  }, [isKonamiMaster])

  const handleClearPreviewClick = useCallback(() => {
    fetch('/api/contentful/clearPreview').then(() => reload())
  }, [reload])

  const handleForceAdsClick = useCallback(() => {
    if (isForcingAd) {
      deleteCookie(AD_TESTING_COOKIE)
      setIsForcingAd(false)
    } else {
      setCookie(AD_TESTING_COOKIE, 'force', { maxAge: Durations.ONE_DAY_IN_SECONDS })
      setIsForcingAd(true)
    }
  }, [isForcingAd])

  return (
    <div className="flex flex-row items-center space-x-4" onClick={increaseTapCount}>
      <AngelLogo color={logoColor} />
      {isKonamiMaster ? (
        <>
          <div className="w-fit cursor-pointer text-lg" onClick={() => setDialogOpen(true)}>
            Developer
          </div>
          <AngelDialog
            isDarkMode
            open={isDialogOpen}
            className="!h-screen"
            bodyClassName="!h-[94%] !w-[94%] -translate-y-[53%] !my-0 rounded-2xl !bg-core-gray-900"
            onClose={() => {
              setDialogOpen(false)
            }}
            title="Developer Tools"
          >
            <div className="flex h-[95%] w-full flex-col gap-4 overflow-x-hidden overflow-y-scroll px-1 scrollbar-thin scrollbar-track-[#2a2824] scrollbar-thumb-core-gray-800">
              <div className="flex flex-col items-center gap-4 lg:flex-row">
                <TitleMD className="min-w-[180px]">User Information:</TitleMD>
                <ParagraphSM as={AsDiv} color="core-gray-500">
                  Anonymous ID: {anonymousId}
                </ParagraphSM>
                {userId && (
                  <ParagraphSM as={AsDiv} color="core-gray-500">
                    User ID: {userId}
                  </ParagraphSM>
                )}
              </div>

              <div className="flex flex-col items-center gap-4 pt-1 lg:flex-row">
                <TitleMD className="min-w-[180px]">Custom Actions:</TitleMD>
                <StyledButton onClick={handleClearPreviewClick}>Clear Preview</StyledButton>
                <StyledButton onClick={handleForceAdsClick}>
                  {isForcingAd ? 'Stop Forcing Ads' : 'Force Ads in Video Player'}
                </StyledButton>
              </div>
            </div>
          </AngelDialog>
        </>
      ) : null}
    </div>
  )
}

const StyledButton: React.FC<Pick<ButtonProps, 'children' | 'onClick'>> = ({ children, onClick }) => {
  return (
    <Button className="justify-center text-core-gray-100 ring-transparent" variant="gray" onClick={onClick}>
      {children}
    </Button>
  )
}

export const ExperimentVariantPicker: React.FC<ExperimentVariantPickerProps> = ({ logoColor }) => {
  if (isDevMode()) return <InternalExperimentVariantPicker logoColor={logoColor} />

  return (
    <LazyHydrate on="click">
      <InternalExperimentVariantPicker logoColor={logoColor} />
    </LazyHydrate>
  )
}
